import React, { useState } from 'react';
import clsx from 'clsx';
import { Sparkles, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { ProcessedWellbeingData } from '../../types/reports';
import { MetricCardSkeleton } from '../ui/Skeleton';
import { EmptyValue } from '../ui/EmptyValue';
import { getMetricLabels, WELLBEING_METRICS } from '../../utils/wellbeingDataProcessor';
import { WellbeingMetricModal } from './WellbeingMetricModal';

interface EmployeeWellbeingProps {
  data: ProcessedWellbeingData | null;
  comparisonData?: ProcessedWellbeingData | null;
  loading?: boolean;
}

interface WellnessMetricData {
  current: number | null;
  trend: number;
  isPositive: boolean;
  previous: number | null; // Previous value for tooltip
}

interface WellnessInsights {
  mood: WellnessMetricData;
  stress: WellnessMetricData;
  workLifeBalance: WellnessMetricData;
  focus: WellnessMetricData;
  energy: WellnessMetricData;
  social: WellnessMetricData;
  sleep: WellnessMetricData;
}

const useWellnessInsights = (
  data: ProcessedWellbeingData | null, 
  comparisonData?: ProcessedWellbeingData | null
): WellnessInsights | null => {
  if (!data || !data.overall.length) return null;
  
  const getMetricData = (key: string): WellnessMetricData => {
    const metric = data.overall.find(m => m.key === key);
    if (!metric) return { current: null, trend: 0, isPositive: false, previous: null };
    
    const current = metric.average;
    let trend = 0;
    let previous = null;
    
    // Always prioritize comparison data for trend calculation
    if (comparisonData) {
      const comparisonMetric = comparisonData.overall.find(m => m.key === key);
      if (comparisonMetric && comparisonMetric.average !== null && current !== null) {
        previous = comparisonMetric.average;
        trend = current - comparisonMetric.average; // Show absolute difference in rating points
      }
    } else if (metric.trend !== null && metric.trend !== undefined) {
      // Fallback to internal trend calculation only if no comparison data
      // Convert percentage trend to absolute value if needed
      trend = current !== null ? (metric.trend / 100) * current : metric.trend;
    }
    
    return { 
      current, 
      trend, 
      isPositive: trend >= 0,
      previous
    };
  };
  
  return {
    mood: getMetricData('wellbeing_mood_state'),
    stress: getMetricData('wellbeing_stress_level'),
    workLifeBalance: getMetricData('wellbeing_work_life_balance'),
    focus: getMetricData('wellbeing_focus'),
    energy: getMetricData('wellbeing_energy'),
    social: getMetricData('wellbeing_social_support'),
    sleep: getMetricData('wellbeing_sleep'),
  };
};

const getMetricColor = (data: WellnessMetricData | undefined, isStressMetric = false) => {
  if (!data || data.current === null || data.current === undefined) {
    return 'bg-rm-bg-50 text-rm-neutral-600 border-rm-neutral-600/20';
  }
  
  const value = data.current;
  
  // Color coding based on KPI values (0-10 scale):
  // For stress metrics: lower values are better (opposite logic)
  // For other metrics: higher values are better
  if (isStressMetric) {
    // Stress: 0-3 = Low/Good (Green), 4-6 = Medium (Yellow), 7-10 = High/Bad (Red)
    if (value <= 3) {
      return 'bg-green-50 text-green-700 border-green-200'; // Low stress = good = green
    } else if (value <= 6) {
      return 'bg-yellow-50 text-yellow-700 border-yellow-200'; // Medium stress = warning = yellow
    } else {
      return 'bg-red-50 text-red-700 border-red-200'; // High stress = bad = red
    }
  } else {
    // Other metrics: 7-10 = Good (Green), 5-6.9 = Medium (Yellow), 0-4.9 = Poor (Red)
    if (value >= 7) {
      return 'bg-green-50 text-green-700 border-green-200'; // High value = good = green
    } else if (value >= 5) {
      return 'bg-yellow-50 text-yellow-700 border-yellow-200'; // Medium value = warning = yellow
    } else {
      return 'bg-red-50 text-red-700 border-red-200'; // Low value = bad = red
    }
  }
};

const MetricCard: React.FC<{
  label: string;
  data: WellnessMetricData | undefined;
  isStressMetric?: boolean;
  comparisonData?: ProcessedWellbeingData | null;
  onClick?: () => void;
}> = ({ label, data, isStressMetric = false, comparisonData, onClick }) => {
  const colorClass = getMetricColor(data, isStressMetric);
  
  return (
    <div 
      className={clsx(
        'rounded-card border p-4 min-w-[140px]',
        colorClass,
        onClick && 'cursor-pointer hover:ring-2 hover:ring-rm-accent-500/30 transition-all'
      )}
      onClick={onClick}
    >
      <p className="text-xs font-semibold uppercase tracking-wide mb-2 whitespace-nowrap pr-2">{label}</p>
      <div className="space-y-2">
        <p className="text-2xl font-semibold">
          {data?.current !== null && data?.current !== undefined ? data.current.toFixed(1) : <EmptyValue size="lg" />}
          {data?.current && <span className="ml-1 text-sm font-normal opacity-70">/ 10</span>}
        </p>
        {comparisonData ? (
          data?.trend !== undefined && data.trend !== 0 && Math.abs(data.trend) >= 0.01 ? (
            <div 
              className={clsx(
                'inline-flex items-center gap-1 text-xs font-semibold',
                // For stress metrics: positive trend (increasing stress) = bad (red), negative trend (decreasing stress) = good (green)
                // For other metrics: positive trend = good (green), negative trend = bad (red)
                isStressMetric 
                  ? (data.trend > 0 ? 'text-red-600' : 'text-emerald-600')
                  : (data.trend > 0 ? 'text-emerald-600' : 'text-red-600')
              )}
              title={`Previous: ${data.previous?.toFixed(1) ?? 'N/A'}/10 (${comparisonData.summary.dateRange.startDate} - ${comparisonData.summary.dateRange.endDate})${isStressMetric ? ' (lower stress is better)' : ''}`}
            >
              {data.trend > 0 ? (
                <ArrowUpRight className="h-3 w-3" />
              ) : (
                <ArrowDownRight className="h-3 w-3" />
              )}
              {data.trend >= 0 ? '+' : ''}{data.trend.toFixed(1)} pts
            </div>
          ) : (
            <p className="text-xs text-rm-neutral-600">No data changes</p>
          )
        ) : (
          <p className="text-xs text-rm-neutral-600 italic">No data changes</p>
        )}
      </div>
    </div>
  );
};

const EmployeeWellbeing: React.FC<EmployeeWellbeingProps> = ({ 
  data, 
  comparisonData,
  loading = false
}) => {
  const wellnessInsights = useWellnessInsights(data, comparisonData);
  const [selectedMetric, setSelectedMetric] = useState<{ key: string; label: string } | null>(null);

  // Show skeleton loading state
  if (loading) {
    return (
      <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-full bg-rm-teal-600/10 p-2 text-rm-teal-600">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Employees Wellbeing</h2>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7">
          {Array.from({ length: 7 }).map((_, index) => (
            <MetricCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  // Show empty state when no data
  if (!data || !wellnessInsights) {
    // Use centralized labels from wellbeingDataProcessor
    const emptyMetrics = getMetricLabels();

    return (
      <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-full bg-rm-teal-600/10 p-2 text-rm-teal-600">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Employees Wellbeing</h2>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7">
          {emptyMetrics.map((label, index) => (
            <div key={index} className="rounded-card border border-rm-neutral-600/20 bg-rm-bg-50 text-rm-neutral-600 p-4 min-w-[140px]">
              <p className="text-xs font-semibold uppercase tracking-wide mb-2 whitespace-nowrap pr-2">{label}</p>
              <div className="space-y-2">
                <p className="text-2xl font-semibold">
                  <EmptyValue size="lg" />
                  <span className="ml-1 text-sm font-normal opacity-70">/ 10</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Create metrics array using centralized configuration
  const metricMapping = [
    { localKey: 'mood', dbKey: 'wellbeing_mood_state', data: wellnessInsights.mood },
    { localKey: 'stress', dbKey: 'wellbeing_stress_level', data: wellnessInsights.stress },
    { localKey: 'workLifeBalance', dbKey: 'wellbeing_work_life_balance', data: wellnessInsights.workLifeBalance },
    { localKey: 'focus', dbKey: 'wellbeing_focus', data: wellnessInsights.focus },
    { localKey: 'energy', dbKey: 'wellbeing_energy', data: wellnessInsights.energy },
    { localKey: 'social', dbKey: 'wellbeing_social_support', data: wellnessInsights.social },
    { localKey: 'sleep', dbKey: 'wellbeing_sleep', data: wellnessInsights.sleep }
  ];

  const metrics = metricMapping.map(({ localKey, dbKey, data }) => {
    const config = WELLBEING_METRICS.find(m => m.key === dbKey);
    return {
      key: localKey,
      label: config?.label || localKey,
      data,
      isStressMetric: config?.invertedScale || false
    };
  });

  return (
    <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-full bg-rm-teal-600/10 p-2 text-rm-teal-600">
          <Sparkles className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold text-rm-primary-700">Employees Wellbeing</h2>
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7">
        {metrics.map(({ key, label, data, isStressMetric }) => {
          // Map local keys back to DB keys for modal
          const dbKeyMap: Record<string, string> = {
            mood: 'wellbeing_mood_state',
            stress: 'wellbeing_stress_level',
            workLifeBalance: 'wellbeing_work_life_balance',
            focus: 'wellbeing_focus',
            energy: 'wellbeing_energy',
            social: 'wellbeing_social_support',
            sleep: 'wellbeing_sleep'
          };
          
          // Only allow click if there's data
          const hasData = data?.current !== null && data?.current !== undefined;
          
          return (
            <MetricCard 
              key={key} 
              label={label} 
              data={data} 
              isStressMetric={isStressMetric}
              comparisonData={comparisonData}
              onClick={hasData ? () => setSelectedMetric({ key: dbKeyMap[key], label }) : undefined}
            />
          );
        })}
      </div>

      {/* Metric Detail Modal */}
      {selectedMetric && data && (
        <WellbeingMetricModal
          metricKey={selectedMetric.key}
          metricLabel={selectedMetric.label}
          data={data}
          comparisonData={comparisonData}
          isOpen={true}
          onClose={() => setSelectedMetric(null)}
        />
      )}
    </div>
  );
};

export default EmployeeWellbeing;